//CRN_20260724 - ⚠️ ngOnInit() called for components/directives & not services ⚠️

import { AccountManagerDTO } from 'src/app/core/models/system/account-manager/account-manager.dto';
import CustomStore from 'devextreme/data/custom_store';
import { environment } from 'src/environments/environment';
import { eSimApiPaths } from 'src/app/core/consts';
import { EncryptionService } from '../../general/encryption-service';
import { ExceptionsService } from '../../general/exceptions-service';
import { HttpClientService } from '../../general/http-client-service';
import { HttpResponse } from '@angular/common/http';
import { IdAndDescriptionDTO } from 'src/app/core/interfaces/general/id-description';
import { Injectable } from '@angular/core';
import { lastValueFrom, map, Observable, of, switchMap } from 'rxjs';
import { StringResultDTO } from 'src/app/core/interfaces/general/string-result';

@Injectable({ providedIn: 'root' })

export class AccountManagersService {

  constructor(
    private encryptionService: EncryptionService,
    private exceptionsService: ExceptionsService,
    private httpClientService: HttpClientService
  ) { }

  public delete(id: number): Observable<any> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>id, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const idEncSS=encResult.result;

                            return this.httpClientService.delete(environment.eSimApi
                                                                 + eSimApiPaths.AccountManagers
                                                                 + "?idEncSS=" + idEncSS);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getAll(): Observable<AccountManagerDTO[]> {
    try {
      return this.httpClientService.get<AccountManagerDTO[]>(environment.eSimApi
                                                             + eSimApiPaths.AccountManagers);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getById(id: number): Observable<AccountManagerDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>id, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const idEncSS=encResult.result;

                            return this.httpClientService.getById(environment.eSimApi
                                                                  + eSimApiPaths.AccountManagers + "/"
                                                                  + idEncSS
                                                                  , undefined);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public post(accountManager: AccountManagerDTO): Observable<AccountManagerDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(accountManager))
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const objectEnc=encResult.result;

                            return this.httpClientService.post(environment.eSimApi
                                                               + eSimApiPaths.AccountManagers
                                                               + "?objectEnc=" + objectEnc
                                                               , {});
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public put(accountManager: AccountManagerDTO): Observable<AccountManagerDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(accountManager))
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const objectEnc=encResult.result;

                            return this.httpClientService.put(environment.eSimApi
                                                              + eSimApiPaths.AccountManagers
                                                              + "?id=" + accountManager.id
                                                              + "&objectEnc=" + objectEnc
                                                              , undefined
                                                              , {}
                                                              , undefined);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  //#region Dropdown lookup

  public getIdAndDescription(): Observable<IdAndDescriptionDTO[]> {
    try {
      return this.httpClientService.get<AccountManagerDTO[]>(environment.eSimApi
                                                             + eSimApiPaths.AccountManagers + "/"
                                                             + eSimApiPaths.AccountManagersIdAndDescription)
             .pipe(
                map((x: AccountManagerDTO[]) => x.map(this.idAndDescriptionConverter))
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  private idAndDescriptionConverter(accountManager: AccountManagerDTO): IdAndDescriptionDTO {
    return { id: accountManager.id, description: accountManager.name };
  }

  public idAndDescriptionLookupDataSource(): {} {
    try {
      return {
        store: new CustomStore({
          key: "id",
          loadMode: "raw",
          load: () => {
            return lastValueFrom(this.getIdAndDescription());
          }
        }),
        sort: "description"
      };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return {};
    }
  }

  //#endregion

  //#region Photo

  public deletePhoto(id: number): Observable<any> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>id, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const idEncSS=encResult.result;

                            return this.httpClientService.delete(environment.eSimApi
                                                                 + eSimApiPaths.AccountManagers + "/"
                                                                 + eSimApiPaths.AccountManagersPhoto
                                                                 + "?idEncSS=" + idEncSS);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getPhotoObjectUrl(photoUrl: string): Observable<string> {
    try {
      const relative=photoUrl.replace(/^\//, "").replace(/^api\//, "");
      const path    =photoUrl.startsWith("http") ? photoUrl : environment.eSimApi + relative;

      return this.httpClientService.getBlob(path)
             .pipe(
                map((response: HttpResponse<Blob>) => URL.createObjectURL(<Blob>response.body))
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of("");
    }
  }

  public uploadPhoto(id: number, photoUpload: File): Observable<AccountManagerDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>id, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const idEncSS =encResult.result;
                            const formData=new FormData();

                            formData.append("photo", photoUpload, photoUpload.name);

                            return this.httpClientService.post(environment.eSimApi
                                                               + eSimApiPaths.AccountManagers + "/"
                                                               + eSimApiPaths.AccountManagersPhoto
                                                               + "?idEncSS=" + idEncSS
                                                               , formData);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  //#endregion
}
